/**
 * formHelpers.js - Helpers de UI para formularios
 * Zero hardcode - Estilos desde sisconfig
 */

/**
 * Calcula grid column span
 * @param {string} gridSpan - 'full', 'half', '1/3', '2/3'
 * @param {object} gridConfig - { columns: number }
 * @returns {number} Columnas a ocupar
 */
export const getColSpan = (gridSpan, gridConfig = { columns: 6 }) => {
  if (!gridSpan || gridSpan === 'full') return gridConfig.columns;
  
  const spanMap = {
    'half': Math.floor(gridConfig.columns / 2),
    '1/3': Math.floor(gridConfig.columns / 3),
    '2/3': Math.floor(gridConfig.columns * 2 / 3)
  };
  
  return spanMap[gridSpan] || 1;
};

/**
 * Aplica estilos de input desde sisconfig
 * @param {object} inputStyles - formulario_ui.inputs
 * @param {boolean} disabled - Deshabilitado
 * @param {boolean} hasError - Tiene error
 * @returns {object} Estilos inline
 */
export const applyInputStyles = (inputStyles = {}, disabled = false, hasError = false) => ({
  height: inputStyles.height || '32px',
  fontSize: inputStyles.fontSize || '0.75rem',
  fontFamily: inputStyles.fontFamily || 'inherit',
  paddingLeft: inputStyles.padding?.x || '0.75rem',
  paddingRight: inputStyles.padding?.x || '0.75rem',
  paddingTop: inputStyles.padding?.y || '0.5rem',
  paddingBottom: inputStyles.padding?.y || '0.5rem',
  borderRadius: inputStyles.borderRadius || '6px',
  borderWidth: inputStyles.borderWidth || '1px',
  borderColor: hasError ? '#ef4444' : (inputStyles.borderColor || '#d1d5db'),
  backgroundColor: disabled ? '#f3f4f6' : (inputStyles.backgroundColor || '#ffffff'),
  color: inputStyles.textColor || '#111827',
  cursor: disabled ? 'not-allowed' : 'text',
  boxSizing: 'border-box'
});

/**
 * Aplica estilos de label desde sisconfig
 * @param {object} labelStyles - formulario_ui.labels
 * @returns {object} Estilos inline
 */
export const applyLabelStyles = (labelStyles = {}) => ({
  display: 'block',
  fontSize: labelStyles.fontSize || '0.875rem',
  fontWeight: labelStyles.fontWeight || '500',
  color: labelStyles.textColor || '#374151',
  marginBottom: labelStyles.marginBottom || '0.5rem'
});

/**
 * Evalúa una condición declarativa (visible_when / disabled_when) contra una
 * fila de datos. Mismo shape en ambos usos: {field, equals|not_equals|in|
 * not_in|is_null|not_null|less_than|greater_or_equal} o combinadores
 * {and:[...]}/{or:[...]}.
 * @param {object} rule - Condición desde sqlexec_acciones/schema_form
 * @param {object} row - Fila contra la que se evalúa (parentRow, etc.)
 * @returns {boolean}
 */
export const evaluarCondicion = (rule, row) => {
  if (!rule) return true;
  const value = row?.[rule.field];
  if (rule.equals !== undefined)     return String(value) === String(rule.equals);
  if (rule.not_equals !== undefined) return String(value) !== String(rule.not_equals);
  if (rule.in)                       return rule.in.map(String).includes(String(value));
  if (rule.not_in)                   return !rule.not_in.map(String).includes(String(value));
  if (rule.is_null === true)         return value === null || value === undefined || value === '';
  if (rule.not_null === true)        return value !== null && value !== undefined && value !== '';
  if (rule.less_than !== undefined)         return Number(value) < Number(rule.less_than);
  if (rule.greater_or_equal !== undefined)  return Number(value) >= Number(rule.greater_or_equal);
  if (rule.and) return rule.and.every(r => evaluarCondicion(r, row));
  if (rule.or)  return rule.or.some(r => evaluarCondicion(r, row));
  return true;
};

/**
 * Aplica `transformar` (mayusculas, sanitizacion...) al texto de un input o
 * textarea controlado y conserva la seleccion (inicio, fin y direccion).
 * Problema: si el valor guardado difiere de lo tecleado, React reescribe el
 * campo al confirmar el estado y el navegador manda el cursor al final.
 * Solucion, sincrona: se escribe aqui mismo el valor transformado en el campo
 * y se fija la seleccion. Al confirmar, React encuentra el campo ya igual al
 * estado y no lo reescribe. No queda ninguna restauracion pendiente que pueda
 * pisar teclas (End, flechas), clics o cambios posteriores.
 * Cada extremo de la seleccion se recalcula transformando el texto previo a
 * el, asi que es correcto aunque se eliminen caracteres antes o despues.
 * @param {HTMLInputElement|HTMLTextAreaElement} target - e.target del onChange
 * @param {(texto: string) => string} transformar - misma transformacion que se guarda
 * @returns {string} el valor transformado, listo para guardar en el estado
 */
export const conservarCursor = (target, transformar) => {
  const crudo = target?.value ?? '';
  const nuevo = transformar(crudo);
  if (!target || typeof nuevo !== 'string' || nuevo === crudo) return nuevo;
  const inicio = target.selectionStart;
  const fin = target.selectionEnd;
  const direccion = target.selectionDirection || 'none';
  target.value = nuevo;
  if (inicio !== null && inicio !== undefined && fin !== null && fin !== undefined) {
    const posicion = (p) => Math.min(nuevo.length, String(transformar(crudo.slice(0, p)) ?? '').length);
    try { target.setSelectionRange(posicion(inicio), posicion(fin), direccion); } catch (e) { /* tipo sin seleccion */ }
  }
  return nuevo;
};

/**
 * Compensa la escala automatica de la interfaz (zoom sobre <html>, ver
 * index.js) en una medida CSS: cada termino vh/vw se divide entre --ui-escala
 * para conservar su tamano real en pantalla; px, rem y % no cambian. Sin
 * escala (variable ausente) la medida equivale a la original.
 * @param {*} valor - medida CSS, p. ej. '95vh' o 'calc(95vh - 120px)'
 * @returns {*} la medida compensada; si no es texto, sin cambios
 */
export const compensarEscalaVh = (valor) => (
  typeof valor === 'string'
    ? valor.replace(/(\d*\.?\d+)(vh|vw)\b/g, 'calc($1$2 / var(--ui-escala, 1))')
    : valor
);
